// deps
import classNames from "classnames";
import React, { memo, ReactElement, useMemo, useCallback } from "react";

//internal-deps
import { AriaHidden, format } from "@swiggy-private/core-ui";

// helpers
import {
    getPaymentMethodPromoMsg,
    getPaymentMethodDisableMsg,
    getPaymentMethodDisplayName,
    canEnablePaymentMethod,
} from "@payment-ui/helpers/Payment";
import {
    isInfoMessageVisible,
    getSwiggyInstrumentAriaLabel,
    getSwiggyInstrumentIconUrl,
    getInfoMessage,
    isLowBalanceAlertVisible,
    isDebitLimitExceeded,
    isDisabledMessageVisible,
} from "@payment-ui/helpers/SwiggyInstrument";

// interfaces
import {
    PaymentMethodProps,
    SwiggyInstrumentsPaymentGroup,
    SwiggyInstrumentsPaymentMethodData,
} from "@payment-ui/interfaces";

// components
import ActionButton from "./components/ActionButton";

//widget
import Alert from "@payment-ui/widgets/alert/Alert";
import PaymentMethodIcon from "@payment-ui/widgets/payment-method-icon/PaymentMethodIcon";
import PaymentMethodName from "@payment-ui/widgets/payment-method-name/PaymentMethodName";
import { ToggleSwitch } from "@payment-ui/widgets/toggle-switch";
import { SwiggyMoneyInfo } from "@payment-ui/widgets/index";

// constants
import { TEXTS } from "@payment-ui/constants/index";

// test-ids
import { SWIGGY_INSTRUMENTS_PAYMENT_METHOD_TEST_IDS } from "@swiggy-private/payment-test-ids";

//styles
import styles from "./SwiggyInstruments.scss";
import { PAYMENT_CODES } from "@swiggy-private/payment-types";

export interface SwiggyInstrumentsProps extends PaymentMethodProps {
    methodData: SwiggyInstrumentsPaymentMethodData;
    paymentActions: SwiggyInstrumentsPaymentGroup["paymentActions"];
    paymentAmount: string;
    paymentGroupBanner?: ReactElement | null;
}

const PAYMENT_ICON_WIDTH = 32;

function SwiggyInstrumentsPaymentMethod(props: SwiggyInstrumentsProps): ReactElement | null {
    const { methodData, paymentActions, paymentAmount, paymentGroupBanner } = props;

    const { onSelect, onPay, getSwiggyInstrumentsData, swiggyMoneyInfoActions } = paymentActions;
    const swiggyInstrumentsData = getSwiggyInstrumentsData(methodData);
    const enabled = useMemo(() => canEnablePaymentMethod(methodData, props.isPaymentGroupEnabled), [
        props.isPaymentGroupEnabled,
        methodData,
    ]);

    const debitLimitExceeded = useMemo(() => isDebitLimitExceeded(methodData), [methodData]);

    const onClick = useCallback(
        (event: React.MouseEvent): void => {
            event.preventDefault();
            if (!enabled) {
                return;
            }
            onSelect(methodData);
        },
        [onSelect, methodData, enabled],
    );

    const onPayClick = useCallback((): void => {
        onPay(methodData);
    }, [onPay, methodData]);

    const containerClass = classNames(styles.swiggyInsContainer, {
        [styles.paymentMethodDisabled]: !enabled && !debitLimitExceeded,
        [styles.swiggyInsDebitLimitExceeded]: debitLimitExceeded,
    });

    const paymentMethodIcon = useMemo(
        (): ReactElement => (
            <div className={styles.swiggyInsPaymentMethodIcon}>
                <PaymentMethodIcon
                    src={getSwiggyInstrumentIconUrl(methodData)}
                    width={PAYMENT_ICON_WIDTH}
                    data-testid={SWIGGY_INSTRUMENTS_PAYMENT_METHOD_TEST_IDS.ICON}
                    customLogo={methodData.name === PAYMENT_CODES.SWIGGYPAY}
                />
            </div>
        ),
        [methodData],
    );

    const canShowAddMoneyCta = useMemo(() => methodData.showAddMoneyCta && methodData.addMoneyCtaMessage != null, [
        methodData,
    ]);

    const canShowInfoMessage = useMemo(() => isInfoMessageVisible(methodData, props.selected, enabled), [
        enabled,
        methodData,
        props.selected,
    ]);

    const swiggyInstrumentAriaLabel = useMemo(
        () =>
            getSwiggyInstrumentAriaLabel({
                paymentMethodData: props.methodData,
                swiggyInstrumentsData,
                isSelected: props.selected,
                isEnabled: enabled,
            }),
        [props.methodData, props.selected, swiggyInstrumentsData, enabled],
    );

    const infoMessage = useMemo(() => getInfoMessage(methodData), [methodData]);

    const canShowLowBalanceAlert = useMemo(
        () => isLowBalanceAlertVisible(swiggyInstrumentsData, props.selected, enabled),
        [enabled, props.selected, swiggyInstrumentsData],
    );

    const infoMessageAndPayButtonContainerClass = classNames({
        [styles.swiggyInsInfoMessageAndButtonWithAddMoneyCta]: canShowAddMoneyCta,
        [styles.swiggyInsInfoMessageAndButtonWithoutAddMoneyCta]: !canShowAddMoneyCta,
    });

    const toggleSwitchClass = classNames(styles.swiggyInsToggle, {
        [styles.swiggyInsToggleSelectedOrWithLowBalanceAlert]: props.selected || canShowLowBalanceAlert,
    });

    const canShowDisabledMessage = useMemo(() => isDisabledMessageVisible(enabled, methodData), [enabled, methodData]);

    const lowBalanceText = useMemo(
        () => format(TEXTS.INSUFFICIENT_BALANCE_DETAIL, swiggyInstrumentsData.additionalBalanceRequired),
        [swiggyInstrumentsData.additionalBalanceRequired],
    );

    const infoMessageContainer = useMemo(() => {
        return (
            canShowInfoMessage && (
                <div>
                    <SwiggyMoneyInfo
                        info={infoMessage}
                        swiggyMoneyInfoHalfCardDetails={methodData.meta.learnMoreHalfCard}
                        canShowSwiggyMoneyInfoCta={methodData.showLearnMoreHalfCard}
                        infoClassName={styles.swiggyInsInfoMessage}
                        swiggyMoneyInfoCtaClassName={styles.swiggyInsSwiggyMoneyInfoCta}
                        actions={swiggyMoneyInfoActions}
                    />
                </div>
            )
        );
    }, [
        canShowInfoMessage,
        infoMessage,
        methodData.meta.learnMoreHalfCard,
        methodData.showLearnMoreHalfCard,
        swiggyMoneyInfoActions,
    ]);

    return (
        <div
            className={containerClass}
            onClick={onClick}
            data-testid={SWIGGY_INSTRUMENTS_PAYMENT_METHOD_TEST_IDS.CONTAINER}
        >
            {paymentGroupBanner}
            <div className={styles.paymentMethodContainer}>
                {paymentMethodIcon}
                <div className={styles.swiggyInsMainContainer}>
                    <div className={styles.swiggyInsMain}>
                        <PaymentMethodName
                            displayName={methodData.displayName}
                            promoMsg={getPaymentMethodPromoMsg(methodData)}
                            enabled={enabled || debitLimitExceeded}
                            ariaLabel={swiggyInstrumentAriaLabel}
                            data-testid={SWIGGY_INSTRUMENTS_PAYMENT_METHOD_TEST_IDS.NAME}
                            className={styles.swiggyInsTitle}
                        >
                            {canShowDisabledMessage && (
                                <Alert
                                    className={styles.swiggyInsDisable}
                                    message={getPaymentMethodDisableMsg(props.methodData)}
                                    data-testid={SWIGGY_INSTRUMENTS_PAYMENT_METHOD_TEST_IDS.DISABLE_MESSAGE}
                                />
                            )}
                        </PaymentMethodName>

                        {(enabled || debitLimitExceeded) && (
                            <>
                                <AriaHidden
                                    className={styles.swiggyInsBalance}
                                    data-testid={SWIGGY_INSTRUMENTS_PAYMENT_METHOD_TEST_IDS.BALANCE}
                                    as="div"
                                >
                                    {methodData.eligibleBalance}
                                </AriaHidden>

                                {!debitLimitExceeded && (
                                    <ToggleSwitch
                                        selected={props.selected}
                                        data-testid={SWIGGY_INSTRUMENTS_PAYMENT_METHOD_TEST_IDS.TICK}
                                        className={toggleSwitchClass}
                                    />
                                )}
                            </>
                        )}
                    </div>
                    {canShowLowBalanceAlert ? (
                        <Alert
                            message={lowBalanceText}
                            data-testid={SWIGGY_INSTRUMENTS_PAYMENT_METHOD_TEST_IDS.LOW_BALANCE_ALERT}
                            className={styles.swiggyInsLowBalance}
                        />
                    ) : (
                        <div className={infoMessageAndPayButtonContainerClass}>
                            {enabled && props.selected && (
                                <ActionButton
                                    isBalanceSufficient={swiggyInstrumentsData.isBalanceSufficient}
                                    canShowAddMoneyCta={canShowAddMoneyCta}
                                    onAddMoneyClick={paymentActions.onAddMoneyClick}
                                    onAddMoneyView={paymentActions.onAddMoneyView}
                                    onPayClick={onPayClick}
                                    paymentAmount={paymentAmount}
                                    paymentMethodDisplayName={getPaymentMethodDisplayName(methodData)}
                                    addMoneyCtaMessage={methodData.addMoneyCtaMessage}
                                />
                            )}
                            {infoMessageContainer}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default memo(SwiggyInstrumentsPaymentMethod);
